import { SubscriptionStatus } from './../enums/subscriptionStatus';
import { AppResponse } from 'modules/core/interfaces/appResponse';
import { ApiResponse } from './../interfaces/apiResponse';
import { LoginCredentials } from './../interfaces/loginCredentials';
import environment from '@/environment';
import { UserInterface } from './../interfaces/user';
import { AuthStateInterface } from './../interfaces/authState';
import { Module, MutationTree, ActionTree } from 'vuex';
import { RootStateInterface } from '@/store/rootState';
import axios, { AxiosPromise, AxiosResponse } from 'axios';
import AuthorizedApiClient from '../models/authorizedApiClient';
import dayjs from 'dayjs';
import { RegisterFormInterface } from '../interfaces/registerForm';
import store from '@/store';
import { ProfileModel } from '@/modules/profile/classes/profile';
import { LoginAsCustomer } from '../interfaces/loginAsCustomer';
import { LoginBaseOnMobileToken } from '../interfaces/loginBaseOnMobileToken';

const startUser: UserInterface = {
  access_token: undefined,
  MasterAccountId: '',
  subscriptionStatus: SubscriptionStatus.SUB_ENDED,
};

export const state: AuthStateInterface = {
  user: null,
};

export const mutations: MutationTree<AuthStateInterface> = {
  updateUser(oldState: AuthStateInterface, payload: UserInterface) {
    oldState.user = Object.assign({}, oldState.user, payload);
  },
  initUser(oldState: AuthStateInterface) {
    oldState.user = Object.assign({}, startUser);
  },
  logout(oldState: AuthStateInterface) {
    localStorage.removeItem(environment.userLocalStorageKey);
    oldState.user = Object.assign({}, startUser);
  },
};

export const actions: ActionTree<AuthStateInterface, RootStateInterface> = {
  login({ commit, dispatch }, payload: LoginCredentials): AxiosPromise<ApiResponse> {
    const params = new URLSearchParams();
    params.append('grant_type', 'password');
    params.append('client_id', environment.clientId);
    params.append('username', payload.username);
    params.append('password', payload.password);
    params.append('scope', 'labelcall.portal.api');

    const loginPromise = axios.post(
      environment.backendUrl + '/connect/token',
      params,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      },
    );

    loginPromise.then((response: ApiResponse) => {
      if (response.status === 200) {
        if (response.data.access_token) {
          commit('updateUser', { access_token: response.data.access_token });
          dispatch('loginCallback', response.data.access_token);
        }
      }
    });

    return loginPromise;
  },

  loginCallback({commit}, accessToken) {
    axios.post(
      environment.backendUrl + environment.apiPrefix + '/account/GetMasterAccountId',
      null,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': 'Bearer ' + accessToken,
        },
      },
    ).then((otherResponse: AxiosResponse<AppResponse<string>>) => {
      if (otherResponse.status === 200) {
        const appData: AppResponse<string> = otherResponse.data;
        commit('updateUser', { MasterAccountId: appData.Result });

        axios.post(
          environment.backendUrl + environment.apiPrefix + '/account/GetSubscriptionStatus',
          { MasterAccoutId : appData.Result },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + accessToken,
            },
          },
        ).then((subStatusResponse: AxiosResponse<AppResponse<string>>) => {
          if (subStatusResponse.status === 200) {
            const data: AppResponse<string> = subStatusResponse.data;
            commit('updateUser', { subscriptionStatus: data.Result });

            axios.get(
              environment.backendUrl + environment.apiPrefix + '/account/GetProfileInfo',
              {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': 'Bearer ' + accessToken,
                },
              },
            ).then((profileDataReponse: AxiosResponse<AppResponse<ProfileModel>>) => {
              if (profileDataReponse.status === 200) {
                const profileData: AppResponse<ProfileModel> = profileDataReponse.data;
                commit('updateUser', { Name: profileData.Result.name });

                localStorage.setItem(environment.userLocalStorageKey,
                  JSON.stringify({
                    access_token: accessToken,
                    MasterAccountId: appData.Result,
                    subscriptionStatus: data.Result,
                    name: profileData.Result.name,
                  }),
                );
              }
            });
          }
        });
      }
    });
  },

  updateUser({ commit, state }, payload) { // tslint:disable-line
    const requestPromise = AuthorizedApiClient.post('/user/update', payload);
    requestPromise.then((response: ApiResponse) => {
      if (response.status) {
        const updatedPayload = Object.assign(payload, {
          personalDataFilled: response.data.data.personalDataFilled,
        });
        commit('updateUser', updatedPayload);
      }
    });

    return requestPromise;
  },

  register ({ commit, state }, userData: RegisterFormInterface): Promise<boolean | undefined> { // tslint:disable-line
    const registerEndpointUrl = environment.backendUrl + '/portal/v1.0/account/create';

    return axios.post(registerEndpointUrl, userData, {
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(async (response: ApiResponse) => {
      if (response.status === 201) {
        const responseData = response.data;

        if (responseData.IsSuccess) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    });
  },

  resetPassword ({ commit, state }, formData: { Email: string }): Promise<boolean | undefined> { // tslint:disable-line
    const endpointUrl = environment.backendUrl + '/account/SendPasswordResetToken';
    return AuthorizedApiClient.post(endpointUrl, formData).then(async (response: ApiResponse) => {
      if (response != undefined && response.status === 200) {
        const responseData = response.data;

        if (responseData.IsSuccess) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    });
  },

  setNewPassword ({ commit, state }, formData: { Password: string, ConfirmPassword: string }): Promise<boolean | undefined> { // tslint:disable-line
    const endpointUrl = environment.backendUrl  + '/account/SetNewPassword';

    return AuthorizedApiClient.post(endpointUrl, formData).then(async (response: ApiResponse) => {
      if (response.status === 200) {
        const responseData = response.data;

        if (responseData.IsSuccess) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    });
  },

  setAccessToken({ commit, dispatch }, token): void {
    commit('updateUser', { access_token: token });
    dispatch('loginCallback', token);
  },

  setUserLanguage({ commit }, language): void {
    commit('updateUser', { language: language });
  },

  loginAsCustomer({ commit, dispatch }, payload: LoginAsCustomer): Promise<boolean | undefined> {
    const endpointUrl = environment.backendUrl  + '/account/LoginAsCustomer';

    return AuthorizedApiClient.post(endpointUrl, payload).then(async (response: ApiResponse) => {
      if (response.status === 200) {
        const responseData = response.data;

        if (responseData.IsSuccess) {
          commit('updateUser', { access_token: response.data.Result.access_token });
          dispatch('loginCallback', response.data.Result.access_token);
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    });
  },

  loginBaseOnMobileToken({ commit, dispatch }, payload: LoginBaseOnMobileToken): Promise<boolean | undefined> {
    const endpointUrl = environment.backendUrl + '/account/LoginBaseOnMobileToken';

    return AuthorizedApiClient.post(endpointUrl, payload).then(async (response: ApiResponse) => {
      if (response == null) {
        return false;
      }
      if (response.status === 200) {
        const responseData = response.data;

        if (responseData.IsSuccess) {
          commit('updateUser', { access_token: response.data.Result.access_token });
          dispatch('loginCallback', response.data.Result.accessToken);
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    });
  },
};

const namespaced: boolean = true;

export const auth: Module<AuthStateInterface, RootStateInterface> = {
  namespaced,
  state,
  mutations,
  actions,
};
